<i18n locale="en" lang="json">
{
  "enterSearch": "Search",
  "loading": "Result is loaded...",
  "noDataText": "No result found"
}
</i18n>
<i18n locale="nl" lang="json">
{
  "enterSearch": "Zoek",
  "loading": "Resultaat wordt geladen...",
  "noDataText": "Geen resultaat gevonden"
}
</i18n>

<template>
  <v-autocomplete
    v-bind="
      mergeProps($props as Record<string, unknown> & VNodeProps[], $attrs, {
        noDataText: computedNoDataText,
        modelValue: undefined,
        search: undefined,
        ['onUpdate:search']: () => undefined,
        ['onUpdate:model-value']: () => undefined,
      })
    "
    v-model="internalValue"
    v-model:search="internalSearch"
    v-set-test-id="['AutocompleteField', testId || getModelExpression()]"
    clearable
    @blur="onBlur"
  >
    <template v-for="(_, name) in slots" #[name]="slotData">
      <slot :name="name" v-bind="getSlotData(slotData)" />
    </template>
    <template #selection="{ item }">{{
      item ? ((item as any)[itemTitle] as string | undefined) : ''
    }}</template>
    <template #loader="{ isActive }">
      <base-linear-loader :is-visible="isActive" />
    </template>
  </v-autocomplete>
</template>

<script lang="ts" setup>
import type { Directive, VNodeProps } from 'vue';
import { computed, mergeProps, ref, useSlots, watch } from 'vue';
import { isEqual } from 'underscore';
import { useI18n } from 'vue-i18n';
import customDebounce from '@/common/debounce';
import type {
  TestIdInput,
  ActionableComponentProps,
} from '@/common/components/ActionableComponent';
import {
  getModelExpression,
  attrName,
  getTestIdString,
} from '@/common/components/ActionableComponent';
import type {
  FormFieldEmits,
  FormFieldProps,
} from '@/common/components/form/FormField.vue';

const { t } = useI18n();

const getActionableElement = (
  el: Element,
  selector: string,
): Element | undefined => {
  const inputEl = el.querySelector(selector);
  return inputEl || undefined;
};

const setTestId = (el: Element, id: string) => {
  el.setAttribute(attrName, id);
  getActionableElement(el, 'input')?.setAttribute(
    attrName,
    `${id}-query-input`,
  );
  getActionableElement(el, '.v-input__icon')?.setAttribute(
    attrName,
    `${id}-menu-open`,
  );
  getActionableElement(el, 'button.v-icon.fa-times-circle')?.setAttribute(
    attrName,
    `${id}-clear-button`,
  );
};

type Props = ActionableComponentProps &
  Omit<FormFieldProps, 'items'> & {
    search?: string;
    items?: Record<string, unknown>[];
    itemValue?: string;
    itemTitle?: string;
    noDataText?: string;
    loading?: boolean;
  };
const props = withDefaults(defineProps<Props>(), {
  itemTitle: 'title',
  itemValue: 'value',
});

const slots = useSlots();

// Wrap slotData to already be typed
type SlotData = Record<string, unknown>;
function getSlotData(data: unknown): SlotData {
  return (data ?? {}) as SlotData;
}

type Emits = FormFieldEmits & { (e: 'update:search', v?: string): void };
const emit = defineEmits<Emits>();
const vSetTestId: Directive = {
  mounted(el, { value }) {
    const id = getTestIdString(value as TestIdInput);
    setTestId(el, id);
  },
  updated(el, { value }) {
    const id = getTestIdString(value as TestIdInput);
    setTestId(el, id);
  },
};

const internalSearch = ref(props.search);
const internalValue = ref(props.modelValue);

watch(
  () => props.modelValue,
  () => {
    if (isEqual(props.modelValue, internalValue.value)) {
      return;
    }
    internalValue.value = props.modelValue;
  },
);

watch(
  () => [internalSearch.value, internalValue.value],
  customDebounce(
    ([search, value]: [string, unknown]) => {
      if (!isEqual(value, props.modelValue)) {
        emit('update:model-value', value === null ? undefined : value);
      }
      if (
        search !== props.search &&
        (search || !value) &&
        !(props.items || []).find(
          item =>
            item[props.itemTitle] === search && item[props.itemValue] === value,
        )
      ) {
        // only emit when search input is not label of selected option
        emit('update:search', search);
      }
    },
    Math.max(props.debounce || 500, 5),
  ) as never,
);

const computedNoDataText = computed<string>(() => {
  if (!props.search) {
    return t('enterSearch');
  }
  if (props.loading) {
    return t('loading');
  }
  return props.noDataText || t('noDataText');
});

const onBlur = () => {
  if (internalValue.value && !props.items?.length) {
    // unset on blur when no items are selected
    internalValue.value = undefined;
  }
  emit('blur');
};
</script>
